import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';

export interface SupplierInvoicePayment {
  id: number;
  supplierInvoiceId: number;
  paymentDate?: string;
  paymentNotes?: string;
  paymentAmount: number;
  createdAt?: string;
  createdById?: number;
  updatedAt?: string;
  updatedById?: number;
  invoice?: {
    id: number;
    invoiceNo?: string;
    invoiceDate?: string;
    amount?: number;
    supplier?: { id: number; name?: string };
  };
}

function toPaymentsList(raw: unknown): SupplierInvoicePayment[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: SupplierInvoicePayment[] }).records)) {
    return (raw as { records: SupplierInvoicePayment[] }).records;
  }
  return [];
}

export const useSupplierInvoicePayments = (searchParams?: string) => {
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<SupplierInvoicePayment[] | { records: SupplierInvoicePayment[] }>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    { revalidateOnFocus: true, revalidateOnReconnect: true }
  );

  const payments = toPaymentsList(data);
  return {
    data: payments,
    clients: payments,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSupplierInvoicePayment = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupplierInvoicePayment>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true }
  );

  return {
    data,
    client: data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSupplierInvoicesForSelect = () => {
  const { data } = useSWR<{ records?: { id: number; invoiceNo?: string; invoiceDate?: string; amount?: number; supplier?: { name?: string } }[] }>(
    '/supplier-invoices',
    fetcher,
    { revalidateOnFocus: false }
  );
  const invoices = data?.records ?? [];
  return {
    options: invoices.map((inv) => ({
      value: String(inv.id),
      label: [inv.invoiceNo || `#${inv.id}`, inv.supplier?.name, inv.invoiceDate].filter(Boolean).join(' • ') || `Invoice #${inv.id}`,
    })),
  };
};
